import { useEffect, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";

// Batch delay in ms - accumulate updates before processing
const BATCH_DELAY_MS = 1000;

/**
 * Custom hook for real-time client updates with batched processing
 * Only use this when actively viewing the Clients page
 * @param {function} onClientUpdate - Callback when a client is updated
 * @param {function} onClientInsert - Callback when a new client is created
 * @param {function} onClientDelete - Callback when a client is deleted
 * @param {boolean} enabled - Whether the subscription is enabled
 */
export const useRealtimeClients = (
  onClientUpdate,
  onClientInsert = null,
  onClientDelete = null,
  enabled = true,
) => {
  // Refs for batching updates
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const pendingInserts = useRef(new Map());
  const pendingUpdates = useRef(new Map());
  const pendingDeletes = useRef(new Set());
  const batchTimeout = useRef(null);

  // Process all pending events in a single batch
  // Clients are simple - no relationships to fetch, use payload directly
  const processBatch = useCallback(() => {
    const inserts = new Map(pendingInserts.current);
    const updates = new Map(pendingUpdates.current);
    const deletes = new Set(pendingDeletes.current);

    pendingInserts.current.clear();
    pendingUpdates.current.clear();
    pendingDeletes.current.clear();

    // Process deletes
    if (deletes.size > 0 && onClientDelete) {
      deletes.forEach((id) => onClientDelete(id));
    }

    // Process inserts (use payload directly - clients have no relationships)
    if (inserts.size > 0 && onClientInsert) {
      inserts.forEach((payload) => onClientInsert(payload.new));
    }

    // Process updates (use payload directly)
    if (updates.size > 0 && onClientUpdate) {
      updates.forEach((payload) => onClientUpdate(payload.new));
    }
  }, [onClientUpdate, onClientInsert, onClientDelete]);

  // Queue an event for batched processing
  const queueEvent = useCallback(
    (payload) => {
      // Only process events if enabled
      if (!enabledRef.current) return;

      if (payload.eventType === "INSERT" && payload.new && onClientInsert) {
        pendingInserts.current.set(payload.new.id, payload);
      } else if (payload.eventType === "UPDATE" && payload.new && onClientUpdate) {
        pendingUpdates.current.set(payload.new.id, payload);
      } else if (payload.eventType === "DELETE" && payload.old && onClientDelete) {
        pendingDeletes.current.add(payload.old.id);
      }

      // Reset the batch timer
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      batchTimeout.current = setTimeout(processBatch, BATCH_DELAY_MS);
    },
    [processBatch, onClientUpdate, onClientInsert, onClientDelete],
  );

  useEffect(() => {
    // Subscribe to all changes on the clients table
    const channel = supabase
      .channel("clients-realtime")
      .on(
        "postgres_changes",
        {
          event: "*", // Listen to INSERT, UPDATE, DELETE
          schema: "public",
          table: "clients",
        },
        queueEvent,
      )
      .subscribe((status) => {
        if (status === "CHANNEL_ERROR") {
          console.error("Realtime: Failed to subscribe to clients");
        }
      });

    // Cleanup subscription on unmount
    return () => {
      if (batchTimeout.current) {
        clearTimeout(batchTimeout.current);
      }
      supabase.removeChannel(channel);
    };
  }, [queueEvent]);
};
